
import React from "react";
import { FileText, Mail } from "lucide-react";
import PolicySection from "../legal/PolicySection";
import ContactInfo from "../legal/ContactInfo";

const PrivacySections = () => {
  return (
    <>
      <PolicySection id="intro" title="Introduction" icon={<FileText size={20} />} number={1}>
        <p>
          Welcome to Elf Letter Bot (hereinafter referred to as "the Site," "we," "us," or "our"). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website or use our free elf letter generator. Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Site.
        </p>
      </PolicySection>
      
      <PolicySection id="collection" title="Information We Collect" icon={<FileText size={20} />} number={2}>
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-elfRed mb-3">Personal Information:</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>When an adult generates a letter, we send the child's name or nickname, elf name, and supplied details to OpenAI to generate the wording. We store those inputs and the generated letter in our database, along with technical information used to limit abuse. Generated letters and form details are also kept in your browser's session storage while the tab remains open. Clearing the browser session does not delete server records; contact us to request deletion.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>We may collect personal information such as your name and email address if you voluntarily provide it (e.g., by requesting an elf letter to be emailed to you, signing up for a newsletter, or filling out a contact form).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>We do not require you to create a user account to browse blog posts or general content.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>When you request letter delivery, we store your email address, letter, and delivery status to send your PDF, maintain the free-tier limit (currently three PDF deliveries per email address), and prevent abuse. When you request a free letter through the disclosed offer, you’ll also receive occasional holiday emails from The Magic Letter Box. We record the disclosure shown and the date of your request separately from any earlier confirmed subscription. You can unsubscribe anytime using the link in your delivery email. An unsubscribe stops marketing emails and does not prevent requested PDF delivery.</span>
            </li>
          </ul>
        </div>
        
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-elfRed mb-3">Usage and Analytics Information:</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>We use analytics and hosting infrastructure on Cloudflare to automatically collect non-personal information about your visit. This may include your IP address, browser type, referring URLs, pages visited, and the date/time of your visit.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>We track clicks on specific call-to-action (CTA) links to understand user engagement and improve site content.</span>
            </li>
          </ul>
        </div>
        
        <div>
          <h3 className="text-xl font-semibold text-elfRed mb-3">Cookies and Similar Technologies:</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>We may use cookies or similar technologies to store user preferences and personalize your experience.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
              <span>You can control how cookies are used by adjusting your browser settings. Please note that disabling cookies may affect the functionality of the Site.</span>
            </li>
          </ul>
        </div>
      </PolicySection>
      
      <PolicySection id="usage" title="How We Use Your Information" icon={<FileText size={20} />} number={3}>
        <p>We use the information we collect for various purposes, including to:</p>
        <ul className="space-y-2 mt-3">
          <li className="flex items-start gap-2">
            <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
            <span>Provide, maintain, and improve the Site.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
            <span>Deliver the elf letters you request via email and enforce the free-tier limit to keep the service available for everyone.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
            <span>Analyze how users interact with the Site and its content.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
            <span>Monitor and analyze trends, usage, and activities to enhance user experience.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
            <span>Send occasional holiday emails from The Magic Letter Box after a disclosed free-letter request or an earlier confirmed subscription, while honoring unsubscribe requests.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-elfGreen mt-1 flex-shrink-0">•</span>
            <span>Protect the security and integrity of our Site.</span>
          </li>
        </ul>
      </PolicySection>
      
      <PolicySection id="disclosure" title="Disclosure of Your Information" icon={<FileText size={20} />} number={4}>
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-elfRed mb-3">Third-Party Service Providers:</h3>
          <p>We may share your information with third-party vendors or service providers who perform services on our behalf, such as analytics, email marketing, or hosting.</p>
        </div>
        
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-elfRed mb-3">Legal Requirements:</h3>
          <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</p>
        </div>
        
        <div>
          <h3 className="text-xl font-semibold text-elfRed mb-3">Business Transfers:</h3>
          <p>If we undergo a business transaction (e.g., merger, acquisition), your information may be transferred. We will notify you of any change in ownership or use of your personal information.</p>
        </div>
      </PolicySection>

      <PolicySection id="security" title="Data Security" icon={<FileText size={20} />} number={5}>
        <p>
          We take reasonable measures to help protect your information from loss, theft, misuse, and unauthorized access. However, no security measures are 100% secure. By using our Site, you acknowledge that we cannot guarantee the absolute security of your information.
        </p>
      </PolicySection>
      
      <PolicySection id="children" title="Children's Privacy" icon={<FileText size={20} />} number={6}>
        <p>
          Elf Letter Bot is intended for adults, particularly parents and caregivers, and is not directed at children under 13. We do not knowingly collect personal information from children under 13. If we learn that we have inadvertently collected personal information from a child under 13, we will promptly take steps to delete that data.
        </p>
      </PolicySection>

      <PolicySection id="links" title="External Links" icon={<FileText size={20} />} number={7}>
        <p>
          Our Site may contain links to external websites, including references or call-to-action links directing you to the Magic Letter Box platform or other third-party sites. We are not responsible for the content, privacy practices, or security of these external websites. We encourage you to review the privacy policies of these websites before disclosing any personal information.
        </p>
      </PolicySection>

      <PolicySection id="updates" title="Updates to This Policy" icon={<FileText size={20} />} number={8}>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the "Effective Date" at the top of this page. We encourage you to review this Privacy Policy periodically to stay informed about our practices.
        </p>
      </PolicySection>

      <PolicySection id="contact" title="Contact Us" icon={<FileText size={20} />} number={9}>
        <p>
          If you have any questions about this Privacy Policy or our data practices, please contact us at:
        </p>
        <ContactInfo email="support@elfletterbot.com" />
      </PolicySection>
    </>
  );
};

export default PrivacySections;
